import type { Effect } from 'effect';
import { Option, Schema } from 'effect';

import type { CartItemImageView, OrderAdapters } from './orderAdapters';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import * as Atom from 'effect/unstable/reactivity/Atom';

/**
 * Seam-C (workspace#49 option C — layer 2: per-domain module factory, atom cluster).
 *
 * Engine-side order atom cluster, lifted from
 * `apps/rcprofi/src/effect/atom/order/order.ts` (+ `encodeOrderDetailResult`
 * from `.../schema/branded/orderTightened.ts`), generalised over:
 *   - `S` — the tenant's AsyncResult order schema (drives `Atom.serializable`
 *     and the RSC `toCodecJson` codec). Constrained to the atom's exact value
 *     type so the serialisable-schema invariant holds statically.
 *   - `D` / `I` — opaque tenant order-detail / order-item types. The engine
 *     reads them ONLY through `deps.adapters` (see `orderAdapters.ts`).
 *   - `R` / `EO` / `ER` — the tenant runtime's requirements/error and the
 *     `getOrder` flow's error. The whole app-side machinery the lint wall
 *     forbids importing (`RuntimeClientAtom`, `getOrder`, `reactivityKeys`,
 *     image helpers) arrives through `deps`.
 *
 * The three module-scope-eager sites (`Atom.serializable`, `Atom.withReactivity`,
 * `Schema.toCodecJson`) fire when the tenant bind file calls this factory at
 * module scope — that is why the bind lives app-side, where the real schema and
 * runtime exist.
 */
/**
 * A raw checkout item with the tenant's analytics image projection.
 * Cart display components use the curated `CartItem` capability model.
 */
export type CheckoutOrderItem<I> = I & {
  crossSellingItems: never[];
  image: CartItemImageView | null;
  selectedExtras: never[];
  tooltipDescription: string;
};

export type OrderAtomsDeps<D, I extends object, R, EO, ER, S extends Schema.Top> = {
  readonly orderDetailResultSchema: S;
  readonly getOrder: () => Effect.Effect<Option.Option<D>, EO, R>;
  readonly adapters: OrderAdapters<D, I>;
  readonly runtime: Atom.AtomRuntime<R, ER>;
  readonly reactivityKey: string;
  /** Serialisable atom key (`'order'` in rcprofi). */
  readonly atomKey: string;
};

export const makeOrderAtoms = <
  D,
  I extends object,
  R,
  EO,
  ER,
  S extends Schema.Codec<AsyncResult.AsyncResult<Option.Option<D>, EO | ER>, unknown>,
>(
  deps: OrderAtomsDeps<D, I, R, EO, ER, S>
) => {
  /**
   * Enriches an opaque tenant item `I` with the engine view-model fields. The
   * spread preserves `I` exactly, so `D`/`I` never degrade to `unknown` through
   * the factory. `crossSellingItems` / `selectedExtras` / `tooltipDescription`
   * are the current app view-model stubs, kept here so cart-item shape stays
   * behaviour-faithful.
   */
  const transformOrderItems = (detail: D): CheckoutOrderItem<I>[] => {
    const items = deps.adapters.items(detail);
    // The `{ ...item, ... }` spread is required by the seam design: it is what
    // preserves the opaque `I` as `I & { image; ... }`. Object.assign would
    // erase the intersection, so `no-map-spread` is waived.
    // eslint-disable-next-line no-map-spread
    return items.map((item) => ({
      ...item,
      crossSellingItems: [],
      image: deps.adapters.itemImage(item),
      selectedExtras: [],
      tooltipDescription: '',
    }));
  };

  const orderAtom = deps.runtime.atom(deps.getOrder()).pipe(
    // `Atom.serializable` internally wraps `schema` in `Schema.toCodecJson(...)`
    // and uses that for BOTH encode and decode — the same codec the RSC seam
    // uses via `encodeOrderDetailResult` below. Pinned by the codec-symmetry
    // test.
    Atom.serializable({
      key: deps.atomKey,
      schema: deps.orderDetailResultSchema,
    }),
    Atom.withReactivity([deps.reactivityKey]),
    Atom.keepAlive
  );

  /**
   * The last checkout order the backend confirmed.
   * A failed refresh retains the previous successful order for analytics.
   */
  const knownOrderAtom = Atom.map(orderAtom, (orderResult) =>
    Option.flatten(AsyncResult.value(orderResult))
  );

  const checkoutOrderItemsAtom = Atom.map(knownOrderAtom, (orderOption) =>
    Option.match(orderOption, {
      onNone: () => [],
      onSome: (orderData) => transformOrderItems(orderData),
    })
  );

  const orderDetailResultJsonCodec = Schema.toCodecJson(deps.orderDetailResultSchema);

  const encodeOrderDetailResult = (orderData: Option.Option<D>) => {
    const resultValue = AsyncResult.success(orderData, { waiting: false });
    return Schema.encodeSync(orderDetailResultJsonCodec)(resultValue);
  };

  return {
    orderAtom,
    knownOrderAtom,
    checkoutOrderItemsAtom,
    encodeOrderDetailResult,
    orderDetailResultJsonCodec,
  };
};

export type { CartItemImageView, OrderAdapters };
